import React, { useState, useEffect, useCallback, useRef } from 'react';
import { unlockLessonByAd } from '../api/adUnlocksApi';
import { useLanguage } from '../contexts/LanguageContext';
import { languageContent } from '../utils/languageContent';
import { showRewardedAd, isNativeMobile } from '../utils/adMobService';
import { getPremiumSubscriber } from '../stores/adFreeSubscriberStore';

interface Props {
  lessonId: string;
  lessonTitle: string;
  onUnlocked: () => void;
  onClose: () => void;
}

type Phase = 'prompt' | 'watching' | 'success' | 'loading';

// Countdown shown on web (no native SDK); native skips straight to the AdMob fullscreen ad
const WEB_AD_DURATION_SECONDS = 5;

const WatchAdUnlockModal: React.FC<Props> = ({ lessonId, lessonTitle, onUnlocked, onClose }) => {
  const { currentLanguage } = useLanguage();
  const content = languageContent[currentLanguage];

  const [phase, setPhase] = useState<Phase>('prompt');
  const [countdown, setCountdown] = useState(WEB_AD_DURATION_SECONDS);
  const modalInsRef = useRef<HTMLModElement>(null);
  const modalAdPushedRef = useRef(false);

  // Web-only countdown while showing the AdSense slot
  useEffect(() => {
    if (phase !== 'watching' || isNativeMobile()) return;
    if (countdown <= 0) return;

    const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [phase, countdown]);

  // AdSense: wait for modal ins to have width, then push once (avoids width=0 + duplicate errors)
  useEffect(() => {
    if (phase !== 'watching' || isNativeMobile()) {
      modalAdPushedRef.current = false;
      return;
    }
    modalAdPushedRef.current = false; /* new "watching" session */
    const pushWhenSized = () => {
      const el = modalInsRef.current;
      if (!el || modalAdPushedRef.current) return;
      if (el.offsetWidth < 50) {
        requestAnimationFrame(pushWhenSized);
        return;
      }
      modalAdPushedRef.current = true;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).adsbygoogle = (window as any).adsbygoogle || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).adsbygoogle.push({});
      } catch (e) {
        modalAdPushedRef.current = false;
        console.warn('AdSense error in unlock modal', e);
      }
    };
    const id = requestAnimationFrame(() => requestAnimationFrame(pushWhenSized));
    return () => cancelAnimationFrame(id);
  }, [phase]);

  const handleWatchAd = useCallback(async () => {
    if (getPremiumSubscriber()) {
      await unlockLessonByAd(lessonId);
      setPhase('success');
      setTimeout(() => onUnlocked(), 1200);
      return;
    }
    if (isNativeMobile()) {
      // Native: show real AdMob rewarded ad fullscreen
      setPhase('loading');
      await showRewardedAd(
        () => {
          // User earned reward
          unlockLessonByAd(lessonId);
          setPhase('success');
          setTimeout(() => onUnlocked(), 1200);
        },
        () => {
          // Ad failed to load — fall back to web countdown
          setPhase('watching');
          setCountdown(WEB_AD_DURATION_SECONDS);
        }
      );
    } else {
      // Web: show AdSense slot + countdown
      setPhase('watching');
      setCountdown(WEB_AD_DURATION_SECONDS);
    }
  }, [lessonId, onUnlocked]);

  const handleClaimReward = useCallback(() => {
    unlockLessonByAd(lessonId);
    setPhase('success');
    setTimeout(() => onUnlocked(), 1200);
  }, [lessonId, onUnlocked]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && phase !== 'watching' && phase !== 'loading') {
      onClose();
    }
  };

  return (
    <div className="ad-modal-backdrop" onClick={handleBackdropClick}>
      <div className="ad-modal-container">

        {phase === 'prompt' && (
          <>
            <div className="ad-modal-icon">🔓</div>
            <h2 className="ad-modal-title">{content.adUnlockTitle}</h2>
            <p className="ad-modal-lesson-name">"{lessonTitle}"</p>
            <p className="ad-modal-description">{content.adUnlockDescription}</p>
            <p className="ad-modal-note">{content.adUnlockNote}</p>

            <button className="ad-modal-watch-btn" onClick={handleWatchAd}>
              {content.adUnlockWatchBtn}
            </button>
            <button className="ad-modal-cancel-btn" onClick={onClose}>
              {content.adUnlockCancelBtn}
            </button>
          </>
        )}

        {phase === 'loading' && (
          <>
            <div className="ad-modal-icon">📺</div>
            <p className="ad-modal-watching-text">{content.adUnlockWatching}</p>
            <div className="ad-modal-countdown-wrap">
              <div className="ad-modal-spinner" />
            </div>
          </>
        )}

        {phase === 'watching' && (
          <>
            <div className="ad-modal-ad-area">
              {/* Web AdSense slot — on mobile this phase only shows if AdMob fails */}
              <ins
                ref={modalInsRef}
                className="adsbygoogle"
                style={{ display: 'block', width: '100%', minWidth: 280, minHeight: 120 }}
                data-ad-client="ca-pub-7754802589108768"
                data-ad-slot="3522444311"
                data-ad-format="auto"
                data-full-width-responsive="true"
              />
            </div>

            <p className="ad-modal-watching-text">{content.adUnlockWatching}</p>

            <div className="ad-modal-countdown-wrap">
              {countdown > 0 ? (
                <div className="ad-modal-countdown">{countdown}</div>
              ) : (
                <button className="ad-modal-claim-btn" onClick={handleClaimReward}>
                  {content.adUnlockClaimBtn}
                </button>
              )}
            </div>
          </>
        )}

        {phase === 'success' && (
          <>
            <div className="ad-modal-success-icon">🎉</div>
            <h2 className="ad-modal-title">{content.adUnlockSuccess}</h2>
            <p className="ad-modal-lesson-name">"{lessonTitle}"</p>
            <div className="ad-modal-success-loader" />
          </>
        )}

      </div>
    </div>
  );
};

export default WatchAdUnlockModal;
